import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Trans } from '@lingui/react/macro';

// Material UI
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';

// Utils
import { APP_TITLE } from '../_configuration';
import { IMAGES, getImage, SUPPORT_EMAIL, SUPPORT_NUMBER } from '../utils';
import { useRouteError } from 'react-router';

const logo = getImage(IMAGES.SOLVE_LOGO_BLACK_TEXT.ref);

const StyledLogo = styled('img')`
  width: 200px;
  margin-top: 24px;
`;

const ErrorBoundary = () => {
  const err = useRouteError() as Error;

  const chunkFailedMessage = /Loading chunk [\d]+ failed/;
  const hasChunkFailed = err?.message && chunkFailedMessage.test(err?.message);

  const importFailedMessage = /error loading dynamically imported module/;
  const hasImportFailed = err?.message && importFailedMessage.test(err?.message);

  // Route errors are often transient during redirect churn, so the boundary can
  // flash by too fast to read. Log enough to identify it after the fact.
  console.error('[ErrorBoundary]', {
    pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
    message: err?.message,
    stack: err?.stack,
    hasChunkFailed,
    hasImportFailed,
    error: err,
  });

  useEffect(() => {
    if (hasChunkFailed || hasImportFailed) {
      window.location.reload();
    }
  }, [hasChunkFailed, hasImportFailed]);

  return (
    <Container>
      <Grid container spacing={3} justifyContent="center" alignItems="center">
        <Grid container item xs={12} justifyContent="center" alignItems="center">
          <StyledLogo src={logo} alt={APP_TITLE} />
        </Grid>
        <div className="mx-auto min-w-[400px] mt-8">
          <h1 className="text-xl mb-4">
            <Trans>Sorry, something went wrong loading the page</Trans>
          </h1>
          {hasChunkFailed && (
            <p>
              <Trans>Attempting to fix the issue automatically...</Trans>
            </p>
          )}
          <p>
            <Trans>
              If this message persists, please contact support at {SUPPORT_EMAIL} or {SUPPORT_NUMBER}
            </Trans>
          </p>
        </div>
      </Grid>
    </Container>
  );
};

export default ErrorBoundary;
